import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Activity, Flame, Wallet, Gauge, BarChart3, Newspaper, Layers } from "lucide-react";
import { Reveal } from "@/components/landing/primitives";
import { Typewriter } from "@/components/landing/Typewriter";
import { Eyebrow, H2, VERIFY_URL, useVerifyStats } from "./shared";
import { EngineExplainer } from "./EngineExplainer";

const INPUTS = [
  { id: "flow", label: "Order flow & footprint", Icon: BarChart3, core: "strat", out: "setups" },
  { id: "liq", label: "Liquidations", Icon: Flame, core: "strat", out: "setups" },
  { id: "whales", label: "Whale wallets", Icon: Wallet, core: "models", out: "pulse" },
  { id: "funding", label: "Funding & open interest", Icon: Gauge, core: "models", out: "pulse" },
  { id: "volume", label: "Volume surges", Icon: Activity, core: "models", out: "pulse" },
  { id: "news", label: "Breaking news", Icon: Newspaper, core: "research", out: "research" },
  { id: "regime", label: "Market regime", Icon: Layers, core: "strat", out: "setups" },
];
const CORE = [
  { id: "strat", t: "Strategy engine", d: "10+ quant strategies, multiple filters, A-grade gate" },
  { id: "models", t: "Market models", d: "Seven-state regime, compression & pressure models" },
  { id: "research", t: "Research models", d: "Impact scoring, asset scorecards, news reaction" },
];
const OUTPUTS = [
  { id: "setups", t: "Trade setups", d: "High-probability buy & sell ideas, published to Verify" },
  { id: "pulse", t: "Market Pulse", d: "Volatility building before the breakout" },
  { id: "research", t: "Research score", d: "Is this asset worth your capital?" },
];
const LOG = ["[scan] 176 markets · 15m close · regime COMPRESSION", "[filter] 10+ strategies → 23 candidates → A-grade gate", "[fire] BTC LONG · grade A · 2.0R · published to verify", "[resolve] SOL SHORT · +0.4R partial banked · stop → BE"];

const curve = (a, b) => {
  const mx = (a.x + b.x) / 2;
  return `M${a.x},${a.y} C${mx},${a.y} ${mx},${b.y} ${b.x},${b.y}`;
};

const Node = ({ on, nodeRef, children, className = "", ...rest }) => (
  <div ref={nodeRef} {...rest}
    className={`relative border px-4 py-3.5 transition-[border-color,background-color,color] duration-300 ${on ? "border-[#BACD1C]/60 bg-[#BACD1C]/[0.06] text-[#ECEBE4]" : "border-white/[0.07] bg-[#0B0C0F] text-[#9DA3AE]"} ${className}`}>
    {children}
  </div>
);

const Hint = () => (
  <span className="font-mono text-[9px] font-normal uppercase tracking-[0.18em] text-[#5A6170] transition-colors group-hover:text-[#ECEBE4]">Explain ↗</span>
);

const useEngineLines = (wrap, refs, active) => {
  const [paths, setPaths] = useState([]);
  const measure = useCallback(() => {
    const w = wrap.current;
    const inp = refs.current[`in-${active.id}`], core = refs.current[`core-${active.core}`], out = refs.current[`out-${active.out}`];
    if (!w || !inp || !core || !out || window.innerWidth < 1024) return setPaths([]);
    const W = w.getBoundingClientRect();
    const pt = (el, side) => { const r = el.getBoundingClientRect(); return { x: (side === "r" ? r.right : r.left) - W.left, y: r.top + r.height / 2 - W.top }; };
    setPaths([curve(pt(inp, "r"), pt(core, "l")), curve(pt(core, "r"), pt(out, "l"))]);
  }, [wrap, refs, active]);
  useLayoutEffect(() => { measure(); const t = setTimeout(measure, 700); return () => clearTimeout(t); }, [measure]);
  useEffect(() => { window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure]);
  return paths;
};

export const Engine = () => {
  const v = useVerifyStats();
  const [i, setI] = useState(0);
  const [hover, setHover] = useState(false);
  const [explain, setExplain] = useState(null);
  const wrap = useRef(null);
  const refs = useRef({});
  const active = INPUTS[i];
  const paths = useEngineLines(wrap, refs, active);
  useEffect(() => {
    if (hover) return;
    const t = setInterval(() => setI((n) => (n + 1) % INPUTS.length), 2600);
    return () => clearInterval(t);
  }, [hover]);
  const set = (el, key) => { refs.current[key] = el; };
  return (
    <section id="engine" data-testid="engine-section" className="border-t border-white/[0.06] bg-[#08090B] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Eyebrow>The engine</Eyebrow>
          <H2 className="mt-5">The engine never sleeps. <span className="text-[#7D8390]">So you don't have to.</span></H2>
          <p className="mt-5 text-lg leading-relaxed text-[#9DA3AE]">10+ quant strategies and multiple filters scan {v?.markets || 176} markets 24/7 for high-probability buy and sell setups. Hover an input to see where it goes, and click any part of the engine for a plain-English explanation with a live example.</p>
        </Reveal>

        <div ref={wrap} className="relative mt-14 grid gap-10 lg:grid-cols-[1fr_1.1fr_1fr] lg:gap-20" data-testid="engine-diagram" onMouseLeave={() => setHover(false)}>
          <svg className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block" aria-hidden>
            {paths.map((d, n) => (
              <g key={`${active.id}-${n}`}>
                <path d={d} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                <motion.path d={d} fill="none" stroke="#BACD1C" strokeOpacity="0.75" strokeWidth="1.25" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.55, delay: n * 0.45, ease: "easeInOut" }} />
                <motion.circle r="2.5" fill="#BACD1C" initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 1, 0] }} transition={{ duration: 1.6, delay: n * 0.45, repeat: Infinity, repeatDelay: 0.6 }}>
                  <animateMotion dur="1.6s" repeatCount="indefinite" path={d} />
                </motion.circle>
              </g>
            ))}
          </svg>

          <div>
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.24em] text-[#5A6170]">What goes in</p>
            <div className="space-y-2">
              {INPUTS.map((x, n) => (
                <Node key={x.id} on={n === i} nodeRef={(el) => set(el, `in-${x.id}`)} data-testid={`engine-in-${x.id}`}
                  onMouseEnter={() => { setHover(true); setI(n); }} onClick={() => { setHover(true); setI(n); }} className="flex cursor-pointer items-center gap-3 text-[15px]">
                  <x.Icon size={15} className={n === i ? "text-[#BACD1C]" : "text-white/30"} />{x.label}
                </Node>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.24em] text-[#5A6170]">
              TQ engine <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#BACD1C]" />running 24/7</span>
            </p>
            <div className="space-y-3 lg:mt-10">
              {CORE.map((c) => (
                <Node key={c.id} on={c.id === active.core} nodeRef={(el) => set(el, `core-${c.id}`)} data-testid={`engine-core-${c.id}`} onClick={() => setExplain(c.id)} className="group cursor-pointer py-5 hover:border-white/25">
                  <div className="flex items-center justify-between font-display text-lg font-semibold tracking-tight">{c.t}<Hint /></div>
                  <div className="mt-1 font-mono text-[11px] text-[#5A6170]">{c.d}</div>
                </Node>
              ))}
            </div>
            <div className="mt-5 h-[4.8em] overflow-hidden font-mono text-[12px] leading-[1.6] text-[#9DA3AE]" data-testid="engine-log">
              <Typewriter words={LOG} typingSpeed={28} deleteSpeed={10} pause={1600} cursorClassName="text-[#BACD1C]" />
            </div>
          </div>

          <div>
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.24em] text-[#5A6170]">What you get</p>
            <div className="space-y-3 lg:mt-10">
              {OUTPUTS.map((o) => (
                <Node key={o.id} on={o.id === active.out} nodeRef={(el) => set(el, `out-${o.id}`)} data-testid={`engine-out-${o.id}`} onClick={() => setExplain(o.id === "research" ? "research_out" : o.id)} className="group cursor-pointer py-5 hover:border-white/25">
                  <div className="flex items-center justify-between font-display text-lg font-semibold tracking-tight">{o.t}<Hint /></div>
                  <div className="mt-1 font-mono text-[11px] text-[#5A6170]">{o.d}</div>
                </Node>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-px border border-white/[0.07] bg-white/[0.07] md:grid-cols-4" data-testid="engine-core">
          {[["10+", "strategies"], ["7", "regime states"], [String(v?.markets || 176), "markets scanned"], ["24/7", "always scanning"]].map(([a, b]) => (
            <div key={b} className="bg-[#08090B] p-6">
              <div className="font-display text-3xl font-semibold tracking-tight text-[#ECEBE4]">{a}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#5A6170]">{b}</div>
            </div>
          ))}
        </div>
        <a href={VERIFY_URL} data-testid="engine-verify-link" className="group mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#9DA3AE] transition-colors hover:text-[#ECEBE4]">
          Audit every setup at verify.tradrquant.com <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>
      <EngineExplainer id={explain} onClose={() => setExplain(null)} />
    </section>
  );
};
